/**
 * Demo profile keyed to each seeded sign-in flow in the profile menu.
 *
 * Profile follows the active flow, not the identity picker. One person with
 * student + faculty keeps one portrait for that flow while they switch doors.
 */

import { getActiveFlowId, SEEDED_FLOWS } from "@/lib/login-flow"
import {
  getLoginSession,
  identityPickerLabel,
  type WorkspaceRole,
} from "@/lib/login-session"
import { NAV_USER } from "@/lib/mock/navigation"
import { stockPortraitUrl } from "@/lib/stock-portrait"

export interface WorkspaceSessionUser {
  name: string
  email: string
  avatar: string
}

/** Profile for each user-role row in the Sign-in flow submenu. */
export const DEMO_FLOW_PROFILES: Record<string, WorkspaceSessionUser> = {
  "role-faculty": {
    name: "Jamie Chen",
    email: "j.chen@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-faculty"),
  },
  "role-admin": {
    name: "Alex Morgan, Program Admin",
    email: "alex.morgan@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-admin"),
  },
  "role-compliance-admin": {
    name: "Priya Nair",
    email: "priya.nair@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-compliance-admin"),
  },
  "role-super-admin": {
    name: "Alex Morgan, Tenet Admin",
    email: "alex.morgan@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-tenant-admin"),
  },
  "role-super-admin-all-owned": {
    name: "Alex Morgan, Tenet Admin",
    email: "alex.morgan@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-tenant-admin-all-owned"),
  },
  "role-super-admin-all-request-access": {
    name: "Alex Morgan, Tenet Admin",
    email: "alex.morgan@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-tenant-admin-all-request-access"),
  },
  "role-student": {
    name: "Alex Morgan",
    email: "alex.morgan@student.jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-student"),
  },
  "role-student-two-apps": {
    name: "Taylor Brooks",
    email: "t.brooks@student.jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-student-two-apps"),
  },
  "role-student-faculty": {
    name: "Alex Morgan",
    email: "alex.morgan@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-student-faculty"),
  },
  "role-student-admin": {
    name: "Sam Rivera",
    email: "sam.rivera@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-student-admin"),
  },
  "role-faculty-admin": {
    name: "Dana Okonkwo",
    email: "d.okonkwo@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-faculty-admin"),
  },
  "role-student-multi-program": {
    name: "Morgan Ellis",
    email: "m.ellis@student.jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-student-multi-program"),
  },
  "role-student-faculty-admin": {
    name: "Jordan Lee",
    email: "jordan.lee@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-student-faculty-admin"),
  },
  "role-student-split-persona": {
    name: "Riley Park",
    email: "riley.park@jhu.edu",
    avatar: stockPortraitUrl("exxat-demo-role-student-split"),
  },
  "role-one-site": {
    name: "Jordan Kim",
    email: "j.kim@clinicalpartner.org",
    avatar: stockPortraitUrl("exxat-demo-role-one-site"),
  },
  direct: {
    name: NAV_USER.name,
    email: NAV_USER.email,
    avatar: NAV_USER.avatar,
  },
  product: {
    name: NAV_USER.name,
    email: NAV_USER.email,
    avatar: stockPortraitUrl("exxat-demo-flow-product"),
  },
  role: {
    name: NAV_USER.name,
    email: NAV_USER.email,
    avatar: stockPortraitUrl("exxat-demo-flow-pick-role"),
  },
}

function activeDemoFlowId(): string | null {
  const session = getLoginSession()
  if (session.demoFlowId) return session.demoFlowId
  try {
    return getActiveFlowId()
  } catch {
    return null
  }
}

function generatedDemoProfile(flowId: string): WorkspaceSessionUser {
  const flow = SEEDED_FLOWS.find(entry => entry.id === flowId)
  const label = flow?.name ?? flowId.replace(/^role-/, "")
  const slug = flowId.replace(/^role-/, "").replace(/-/g, ".")
  return {
    name: `Demo ${label}`,
    email: `demo.${slug}@jhu.edu`,
    avatar: stockPortraitUrl(`exxat-demo-${flowId}`),
  }
}

function profileForDemoFlow(flowId: string | null): WorkspaceSessionUser | null {
  if (!flowId) return null
  return DEMO_FLOW_PROFILES[flowId] ?? generatedDemoProfile(flowId)
}

/** Profile for one workspace role when no demo flow is known. */
function profileForRoleOnly(role: WorkspaceRole): WorkspaceSessionUser {
  if (role === "student") {
    return {
      name: "Alex Morgan",
      email: "alex.morgan@student.jhu.edu",
      avatar: stockPortraitUrl("exxat-demo-role-student"),
    }
  }
  if (role === "member") {
    return DEMO_FLOW_PROFILES["role-faculty"]!
  }

  const session = getLoginSession()
  const programAdmin =
    Array.isArray(session.administeredProgramIds) && session.administeredProgramIds.length > 0
  if (programAdmin) return DEMO_FLOW_PROFILES["role-admin"]!
  return DEMO_FLOW_PROFILES["role-super-admin"]!
}

/** @deprecated Use {@link workspaceSessionUser}. Kept for identity picker icons only. */
export function workspaceSessionUserForRole(_role: WorkspaceRole): WorkspaceSessionUser {
  return workspaceSessionUser()
}

/** Profile for the signed-in demo flow. Stable while switching identity doors. */
export function workspaceSessionUser(): WorkspaceSessionUser {
  return profileForDemoFlow(activeDemoFlowId()) ?? profileForRoleOnly(getLoginSession().role)
}

/** Short subtitle under the name when the session holds multiple identities. */
export function workspaceSessionUserSubtitle(): string | null {
  const session = getLoginSession()
  if (session.opensAs.length <= 1) return null
  return identityPickerLabel(session.role)
}

export function identityRoleIcon(role: WorkspaceRole): string {
  if (role === "student") return "fa-user-graduate"
  if (role === "member") return "fa-chalkboard-user"
  return "fa-user-shield"
}
